import { useEffect, useRef, useState } from 'react';
import { useStore } from '@tanstack/react-store';
import { useMutation } from '@tanstack/react-query';
import { api } from 'lib/request/api';
import type { MineralType } from 'types/minerals';
import type {
  TypeCurveRatioRecord,
  TypeCurveVolumeRecord,
} from 'types/api-responses';
import { type TcForecastOverride } from '../atoms';
import { useTcDcaForm } from './tc-dca-form-context';
import type { StreamDcaParams, TcDcaFormValues } from './types';

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

/**
 * Convert camelCase to snake_case for API payloads.
 * @param {string} s - The camelCase string.
 * @return {string} The snake_case string.
 */
function toSnakeCase(s: string): string {
  return s.replace(/[A-Z]/g, (c) => `_${c.toLowerCase()}`);
}

const STREAMS: MineralType[] = ['oil', 'gas', 'water'];

const STREAM_FIELD_MAP: Record<MineralType, keyof TcDcaFormValues> = {
  oil: 'oilDcaParams',
  gas: 'gasDcaParams',
  water: 'waterDcaParams',
};

/** DCA model → relevant parameter names (from getModelParameterMapping). */
const DCA_MODEL_PARAMS: Record<string, string[]> = {
  modified_hyperbolic: ['qi', 'di', 'bFactor', 'dlim'],
  hyperbolic: ['qi', 'di', 'bFactor'],
  exponential: ['qi', 'di'],
  ratio: ['ri', 'tElf', 'm'],
};

/** Ramp-up model → relevant ramp-up parameter names. */
const RAMP_UP_MODEL_PARAMS: Record<string, string[]> = {
  constant: ['rampUpQi'],
  linear: ['rampUpQi', 'rampUpN'],
  exponential: ['rampUpQi', 'rampUpN'],
};

/**
 * Ramp-up fields whose validation errors should NOT block the bulk forecast.
 * When these are incomplete the forecast fires with DCA-only; once the user
 * fills them in the `useEffect` re-fires and includes `ramp_up`.
 */
const RAMP_UP_ERROR_FIELDS = ['rampUpQi', 'rampUpDuration', 'rampUpN'];

/**
 * Returns true when a stream has no DCA-model validation errors and has at
 * least `qi` populated (the minimum requirement for the API to produce a
 * forecast).  Ramp-up errors are intentionally ignored — the payload builder
 * simply omits `ramp_up` when its params are incomplete.
 * @param {MineralType} stream - The mineral stream to validate.
 * @param {TcDcaFormValues} formValues - Current form values.
 * @param {Record<string,string>|undefined} errorFields - Field-level error map.
 * @return {boolean} Whether the stream is valid for forecasting.
 */
function isStreamValid(
  stream: MineralType,
  formValues: TcDcaFormValues,
  errorFields: Record<string, string> | undefined
): boolean {
  const fieldKey = STREAM_FIELD_MAP[stream];
  const params = formValues[fieldKey] as StreamDcaParams;

  // Primary model parameter must be populated
  if (params.dcaModel === 'ratio') {
    if (params.ri === null || params.ri === undefined) return false;
  } else {
    if (params.qi === null || params.qi === undefined) return false;
  }

  // Only DCA-model errors block the stream; ramp-up errors are skipped
  if (errorFields) {
    const prefix = `${fieldKey}.`;
    for (const key of Object.keys(errorFields)) {
      if (!key.startsWith(prefix)) continue;
      const fieldName = key.slice(prefix.length);
      if (RAMP_UP_ERROR_FIELDS.includes(fieldName)) continue;
      return false;
    }
  }

  return true;
}

/**
 * Returns true when the ramp-up params are fully populated and should be
 * included in the API payload.  When false the forecast fires DCA-only.
 * @param {StreamDcaParams} params - The stream DCA parameters.
 * @return {boolean} Whether all required ramp-up fields are populated.
 */
function isRampUpComplete(params: StreamDcaParams): boolean {
  if (params.rampUpModel === 'none') return false;
  // All ramp-up models require qi
  if (params.rampUpQi === null) return false;
  // linear and exponential also require n
  if (
    (params.rampUpModel === 'linear' || params.rampUpModel === 'exponential') &&
    params.rampUpN === null
  ) {
    return false;
  }
  return true;
}

/**
 * Build the camelCase DCA params object for a single stream.
 * @param {StreamDcaParams} params - The stream DCA parameters.
 * @param {TcDcaFormValues} formValues - Current form values.
 * @return {Record<string,unknown>} The stream payload for the API.
 */
function buildStreamPayload(
  params: StreamDcaParams,
  formValues: TcDcaFormValues
): Record<string, unknown> {
  const modelParams = DCA_MODEL_PARAMS[params.dcaModel] || [];

  const result: Record<string, unknown> = {
    dcaModel: params.dcaModel,
    durationPeriod: formValues.forecastDurationPeriod,
    durationFormat: 'years',
  };

  // qi-group: qi + format fields travel together
  if (modelParams.includes('qi')) {
    if (params.qi !== null) result.qi = params.qi;
    result.qiFormat = formValues.qiFormat;
  }

  if (modelParams.includes('di') && params.di !== null) result.di = params.di;

  if (modelParams.includes('bFactor') && params.bFactor !== null)
    result.bFactor = params.bFactor;

  if (modelParams.includes('dlim') && params.dlim !== null) {
    result.dlim = params.dlim;
  }

  if (modelParams.includes('ri') && params.ri !== null) result.ri = params.ri;
  if (modelParams.includes('tElf') && params.tElf !== null)
    result.tElf = params.tElf;
  if (modelParams.includes('m') && params.m !== null) result.m = params.m;

  // Ramp-up — only when all required fields are populated
  if (isRampUpComplete(params)) {
    const rampUpParams = RAMP_UP_MODEL_PARAMS[params.rampUpModel] || [];
    result.rampUp = {
      model: params.rampUpModel,
      ...(rampUpParams.includes('rampUpQi') &&
        params.rampUpQi !== null && { qi: params.rampUpQi }),
      ...(params.rampUpDuration !== null && {
        tRamp: params.rampUpDuration,
      }),
      ...(rampUpParams.includes('rampUpN') &&
        params.rampUpN !== null && { n: params.rampUpN }),
    };
  }

  return result;
}

/**
 * Serializes the effective payload + context identifiers into a stable string
 * so the main effect can skip redundant API calls when nothing meaningful
 * changed (e.g. the user toggled an incomplete ramp-up model).
 * @param {number} typeCurveHeaderDetailsId - The type curve header details identifier.
 * @param {number} dcaScenarioId - The DCA scenario ID.
 * @param {Record<string,unknown>} dcaParams - The DCA parameters payload.
 * @param {string[]} uwis - The list of UWIs.
 * @return {string} A stable JSON string key.
 */
function buildFetchKey(
  typeCurveHeaderDetailsId: number,
  dcaScenarioId: number,
  dcaParams: Record<string, unknown>,
  uwis: string[]
): string {
  return JSON.stringify({
    typeCurveHeaderDetailsId,
    dcaScenarioId,
    dcaParams,
    uwis: [...uwis].sort(),
  });
}

/**
 * Extract error field map from the form's errorMap. Returns undefined when
 * there are no field-level errors.
 * @param {Record<string,unknown>|undefined} errorMap - The form error map.
 * @return {Record<string,string>|undefined} Field-level errors, if any.
 */
function extractErrorFields(
  errorMap: Record<string, unknown> | undefined
): Record<string, string> | undefined {
  const onChangeErrors = errorMap?.onChange;
  if (!onChangeErrors || typeof onChangeErrors !== 'object') return undefined;
  return (onChangeErrors as { fields?: Record<string, string> })?.fields;
}

// ---------------------------------------------------------------------------
// Bulk forecasts API types
// ---------------------------------------------------------------------------

interface BulkForecastsRequest {
  query: {
    bulkForecasts: {
      dcaForecastParams: Array<{
        typeCurveHeaderDetailsId: number;
        dcaParams: {
          diFormat: string;
          forecastDurationOption: string;
          oilDcaParams?: Record<string, unknown>;
          gasDcaParams?: Record<string, unknown>;
          waterDcaParams?: Record<string, unknown>;
        };
      }>;
      responseConfig: {
        includeCumulatives: boolean;
        includeRatios: boolean;
        includeMbts: boolean;
        includeEur: boolean;
        includeEurRatios: boolean;
      };
    };
  };
}

interface BulkForecastsResponse {
  totalCount: number;
  results: Record<
    string,
    Array<{
      volumes: TypeCurveVolumeRecord[];
      cumulatives: TypeCurveVolumeRecord[];
      ratios: TypeCurveRatioRecord[];
      mbts: TypeCurveVolumeRecord[];
      eur?: { oil: number; gas: number; water: number };
      eurRatios?: Record<string, number>;
    }>
  >;
}

// ---------------------------------------------------------------------------
// Hook
// ---------------------------------------------------------------------------

/**
 * Snapshot form DCA params into the shape expected by `TcForecastOverride`.
 * camelCase `bFactor` → snake_case `b_factor` to match probit paramKey.
 * @param {TcDcaFormValues} formValues - The current form values.
 * @return {object} DCA params keyed by stream.
 */
function buildDcaParamsSnapshot(
  formValues: TcDcaFormValues
): TcForecastOverride['dcaParams'] {
  const result: TcForecastOverride['dcaParams'] = {};
  for (const stream of STREAMS) {
    const fieldKey = STREAM_FIELD_MAP[stream];
    const params = formValues[fieldKey] as StreamDcaParams;
    result[stream] = {
      qi: params.qi ?? null,
      di: params.di ?? null,
      b_factor: params.bFactor ?? null,
      dlim: params.dlim ?? null,
    };
  }
  return result;
}

interface UseForecastVolumesOptions {
  uwis: string[];
  typeCurveHeaderDetailsId: number | null;
  dcaScenarioId: number;
}

const DEBOUNCE_MS = 500;

export function useForecastVolumes({
  uwis,
  typeCurveHeaderDetailsId,
  dcaScenarioId,
}: UseForecastVolumesOptions) {
  const { form } = useTcDcaForm();

  // Subscribe to form store so the hook re-renders on ANY value change —
  // whether from manual user edits or programmatic setFieldValue (regression).
  const formValues = useStore(form.store, (s) => s.values);
  const errorMap = useStore(form.store, (s) => s.errorMap);
  const isDirty = useStore(form.store, (s) => s.isDirty);

  const [forecastData, setForecastData] = useState<{
    volume: TypeCurveVolumeRecord[];
    cumulative: TypeCurveVolumeRecord[];
    ratio: TypeCurveRatioRecord[];
    materialBalanceTime: TypeCurveVolumeRecord[];
  } | null>(null);

  const [forecastOverride, setForecastOverride] =
    useState<TcForecastOverride | null>(null);

  const debounceTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const prevFetchKeyRef = useRef<string | null>(null);

  // Refs so the mutation onSuccess callback can access current values.
  const tcHeaderIdRef = useRef(typeCurveHeaderDetailsId);
  tcHeaderIdRef.current = typeCurveHeaderDetailsId;
  const formValuesRef = useRef(formValues);
  formValuesRef.current = formValues;

  const mutation = useMutation<
    BulkForecastsResponse,
    Error,
    BulkForecastsRequest
  >({
    mutationFn: (payload) =>
      api
        .post('queries/reservoir/bulk_forecasts', { json: payload })
        .json<BulkForecastsResponse>(),
    onSuccess: (data) => {
      // Extract volumes from the first result key
      const resultKeys = Object.keys(data.results);
      if (resultKeys.length === 0) {
        setForecastData(null);
        setForecastOverride(null);
        return;
      }
      const firstResult = data.results[resultKeys[0]];
      if (!firstResult || firstResult.length === 0) {
        setForecastData(null);
        setForecastOverride(null);
        return;
      }
      const result = firstResult[0];
      setForecastData({
        volume: result.volumes,
        cumulative: result.cumulatives,
        ratio: result.ratios,
        materialBalanceTime: result.mbts,
      });

      const currentTcId = tcHeaderIdRef.current;
      if (currentTcId !== null && currentTcId !== undefined) {
        setForecastOverride({
          typeCurveHeaderDetailsId: currentTcId,
          eur: result.eur ?? null,
          eurRatios: result.eurRatios ?? null,
          cumulatives: result.cumulatives ?? [],
          dcaParams: buildDcaParamsSnapshot(formValuesRef.current),
        });
      }
    },
    onError: () => {
      setForecastData(null);
      setForecastOverride(null);
    },
  });

  // Keep a stable ref to mutation so the effect doesn't depend on it.
  const mutationRef = useRef(mutation);
  mutationRef.current = mutation;

  // Unmount-only cleanup — the main effect no longer returns a cleanup
  // function because an early return (unchanged fetch key) must leave the
  // pending debounce timer intact.
  useEffect(() => {
    return () => {
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
        debounceTimerRef.current = null;
      }
    };
  }, []);

  useEffect(() => {
    // Gate: no TC selected yet — nothing to forecast
    if (
      typeCurveHeaderDetailsId === null ||
      typeCurveHeaderDetailsId === undefined
    ) {
      setForecastData(null);
      setForecastOverride(null);
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
        debounceTimerRef.current = null;
      }
      prevFetchKeyRef.current = null;
      return;
    }

    // Gate: form must have been touched at least once
    if (!isDirty) {
      setForecastData(null);
      setForecastOverride(null);
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
        debounceTimerRef.current = null;
      }
      prevFetchKeyRef.current = null;
      return;
    }

    // Determine which streams are valid right now
    const errorFields = extractErrorFields(
      errorMap as Record<string, unknown> | undefined
    );
    const validStreams = STREAMS.filter((s) =>
      isStreamValid(s, formValues, errorFields)
    );

    if (validStreams.length === 0) {
      setForecastData(null);
      setForecastOverride(null);
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
        debounceTimerRef.current = null;
      }
      prevFetchKeyRef.current = null;
      return;
    }

    // Build the per-stream DCA params — only include valid streams
    const dcaParams: BulkForecastsRequest['query']['bulkForecasts']['dcaForecastParams'][0]['dcaParams'] =
      {
        diFormat: toSnakeCase(formValues.diFormat),
        forecastDurationOption: toSnakeCase(formValues.forecastDurationOption),
      };

    for (const stream of validStreams) {
      const fieldKey = STREAM_FIELD_MAP[stream];
      const params = formValues[fieldKey] as StreamDcaParams;
      const payloadKey = `${stream}DcaParams` as keyof typeof dcaParams;
      (dcaParams as Record<string, unknown>)[payloadKey] = buildStreamPayload(
        params,
        formValues
      );
    }

    // Skip if the effective payload hasn't changed
    const fetchKey = buildFetchKey(
      typeCurveHeaderDetailsId,
      dcaScenarioId,
      dcaParams,
      uwis
    );
    if (fetchKey === prevFetchKeyRef.current) return;
    prevFetchKeyRef.current = fetchKey;

    // Debounce: cancel previous pending call, schedule a new one
    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
    }

    debounceTimerRef.current = setTimeout(() => {
      mutationRef.current.mutate({
        query: {
          bulkForecasts: {
            dcaForecastParams: [
              {
                typeCurveHeaderDetailsId,
                dcaParams,
              },
            ],
            responseConfig: {
              includeCumulatives: true,
              includeRatios: true,
              includeMbts: true,
              includeEur: true,
              includeEurRatios: true,
            },
          },
        },
      });
    }, DEBOUNCE_MS);
  }, [
    formValues,
    errorMap,
    isDirty,
    typeCurveHeaderDetailsId,
    dcaScenarioId,
    uwis,
  ]);

  return {
    forecastData,
    forecastOverride,
    isFetching: mutation.isPending,
  };
}
